import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

interface AccountTransactionStats {
  totalIncome: number;
  totalExpense: number;
  totalTransfersIn: number;
  totalTransfersOut: number;
  netFlow: number;
  transactionCount: number;
}

export function useAccountTransactions(accountId?: string) {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTransactions = useCallback(async () => {
    if (!user || !accountId) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get transactions where this account is either source or destination
      const { data, error: fetchError } = await supabase
        .from('transactions')
        .select(
          `
          *,
          category:categories(*),
          account:accounts!transactions_account_id_fkey(*),
          to_account:accounts!transactions_to_account_id_fkey(*)
        `
        )
        .eq('user_id', user.id)
        .or(`account_id.eq.${accountId},to_account_id.eq.${accountId}`)
        .order('date', { ascending: false });

      if (fetchError) throw fetchError;

      setTransactions(data || []);
    } catch (err) {
      console.error('Error loading account transactions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user, accountId]);

  const calculateStats = useCallback((): AccountTransactionStats => {
    if (!accountId) {
      return {
        totalIncome: 0,
        totalExpense: 0,
        totalTransfersIn: 0,
        totalTransfersOut: 0,
        netFlow: 0,
        transactionCount: 0,
      };
    }

    let totalIncome = 0;
    let totalExpense = 0;
    let totalTransfersIn = 0;
    let totalTransfersOut = 0;

    transactions.forEach((t) => {
      if (t.type === 'income' && t.account_id === accountId) {
        totalIncome += t.amount;
      } else if (t.type === 'expense' && t.account_id === accountId) {
        totalExpense += t.amount;
      } else if (t.type === 'transfer') {
        if (t.account_id === accountId) {
          // Money going out (source)
          totalTransfersOut += t.amount;
        }
        if (t.to_account_id === accountId) {
          // Money coming in (destination)
          totalTransfersIn += t.amount;
        }
      }
    });

    const netFlow = totalIncome + totalTransfersIn - totalExpense - totalTransfersOut;

    return {
      totalIncome,
      totalExpense,
      totalTransfersIn,
      totalTransfersOut,
      netFlow,
      transactionCount: transactions.length,
    };
  }, [transactions, accountId]);

  // Get the direction of a transaction relative to this account
  const getTransactionDirection = useCallback(
    (transaction: Transaction): 'in' | 'out' | 'neutral' => {
      if (!accountId) return 'neutral';

      if (transaction.type === 'income' && transaction.account_id === accountId) {
        return 'in';
      }
      if (transaction.type === 'expense' && transaction.account_id === accountId) {
        return 'out';
      }
      if (transaction.type === 'transfer') {
        if (transaction.account_id === accountId) return 'out';
        if (transaction.to_account_id === accountId) return 'in';
      }
      return 'neutral';
    },
    [accountId]
  );

  // Get amount with proper sign for display
  const getSignedAmount = useCallback(
    (transaction: Transaction): number => {
      const direction = getTransactionDirection(transaction);
      if (direction === 'in') return transaction.amount;
      if (direction === 'out') return -transaction.amount;
      return transaction.amount;
    },
    [getTransactionDirection]
  );

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  return {
    transactions,
    loading,
    error,
    loadTransactions,
    calculateStats,
    getTransactionDirection,
    getSignedAmount,
  };
}

// Hook to get all transactions grouped by account
export function useTransactionsByAccount() {
  const { user } = useAuth();
  const [transactionsByAccount, setTransactionsByAccount] = useState<Map<string, Transaction[]>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(
          `
          *,
          category:categories(*),
          account:accounts!transactions_account_id_fkey(*),
          to_account:accounts!transactions_to_account_id_fkey(*)
        `
        )
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      // Group by account
      const grouped = new Map<string, Transaction[]>();

      (data || []).forEach((t) => {
        if (t.account_id) {
          const existing = grouped.get(t.account_id) || [];
          grouped.set(t.account_id, [...existing, t]);
        }
        if (t.to_account_id && t.to_account_id !== t.account_id) {
          const existing = grouped.get(t.to_account_id) || [];
          grouped.set(t.to_account_id, [...existing, t]);
        }
      });

      setTransactionsByAccount(grouped);
    } catch (err) {
      console.error('Error loading grouped transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    transactionsByAccount,
    loading,
    refresh: loadData,
  };
}
